import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { CheckSquare, Clock, ArrowLeftRight, Bell, Package, AlertTriangle, ClipboardMinus,
         ClipboardCheck, Truck, ShoppingCart, LogIn, LogOut, Loader2, ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export const EmployeeDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [pendingConfirmationsCount, setPendingConfirmationsCount] = useState(0);
  const [mhdStats, setMhdStats] = useState({ expired_today: 0, expiring_soon: 0, total: 0 });
  const [shiftCheckPending, setShiftCheckPending] = useState(false);
  const [orderCategories, setOrderCategories] = useState([]);

  // Schichtloser "Ich bin da" Eincheck-Status
  const [activeSession, setActiveSession] = useState(null);  // { id, started_at } | null
  const [sessionLoading, setSessionLoading] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchPendingConfirmations();
    fetchMhdStats();
    fetchShiftCheckStatus();
    fetchOrderCategories();
    fetchWorkSessionStatus();
  }, []);

  const fetchWorkSessionStatus = async () => {
    try {
      const locIds = user?.location_ids || [];
      if (!locIds.length) return;
      const locId = locIds[0];
      const res = await axios.get(`${API}/work-sessions/active?location_id=${locId}`, { headers: authH() });
      setActiveSession(res.data?.id ? res.data : null);
    } catch { /* silent */ }
  };

  const handleStartSession = async () => {
    const locId = (user?.location_ids || [])[0];
    if (!locId) return;
    setSessionLoading(true);
    try {
      const res = await axios.post(`${API}/work-sessions/start`, { location_id: locId }, { headers: authH() });
      setActiveSession(res.data);
    } catch (e) {
      alert(e.response?.data?.detail || 'Einchecken fehlgeschlagen');
    } finally { setSessionLoading(false); }
  };

  const handleEndSession = async () => {
    const locId = (user?.location_ids || [])[0];
    if (!locId) return;
    setSessionLoading(true);
    try {
      await axios.post(`${API}/work-sessions/end`, { location_id: locId }, { headers: authH() });
      setActiveSession(null);
    } catch (e) {
      alert(e.response?.data?.detail || 'Fehler beim Abmelden');
    } finally { setSessionLoading(false); }
  };

  const fetchStats = async () => {
    try {
      // Bug 2 Fix: neues Endpoint nutzen (task_templates_v2), nicht altes task_instances-System
      const response = await axios.get(`${API}/tasks/dashboard-stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchPendingConfirmations = async () => {
    try {
      const response = await axios.get(`${API}/employee/handover/pending-confirmations/count`);
      setPendingConfirmationsCount(response.data.count || 0);
    } catch (error) {
      console.error('Failed to fetch pending confirmations:', error);
    }
  };

  const fetchMhdStats = async () => {
    try {
      const response = await axios.get(`${API}/mhd/dashboard-stats`);
      setMhdStats(response.data);
    } catch (error) {
      console.error('Failed to fetch MHD stats:', error);
    }
  };

  const fetchShiftCheckStatus = async () => {
    try {
      const userResponse = await axios.get(`${API}/auth/me`);
      const locationIds = userResponse.data.location_ids || [];
      if (locationIds.length > 0) {
        const response = await axios.get(`${API}/shift-check/status?location_id=${locationIds[0]}`);
        setShiftCheckPending(!response.data.completed);
      }
    } catch (error) {
      console.error('Failed to fetch shift check status:', error);
    }
  };

  const fetchOrderCategories = async () => {
    try {
      const response = await axios.get(`${API}/order-categories`);
      setOrderCategories(response.data || []);
    } catch { /* optional */ }
  };

  if (!stats) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  const statCards = [
    {
      title: 'Meine Aufgaben heute',
      value: stats.total_tasks,
      icon: CheckSquare,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      onClick: () => navigate('/employee/daily-tasks')
    },
    {
      title: 'Abgeschlossen',
      value: stats.completed_tasks,
      icon: CheckSquare,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      onClick: null
    },
    {
      title: 'MHD',
      value: mhdStats.expired_today > 0 ? mhdStats.expired_today : (mhdStats.expiring_soon > 0 ? mhdStats.expiring_soon : mhdStats.total),
      subtitle: mhdStats.expired_today > 0 ? 'heute ablaufend!' : (mhdStats.expiring_soon > 0 ? 'bald ablaufend' : 'Produkte'),
      icon: mhdStats.expired_today > 0 ? AlertTriangle : Package,
      color: mhdStats.expired_today > 0 ? 'text-red-600' : (mhdStats.expiring_soon > 0 ? 'text-yellow-600' : 'text-orange-600'),
      bgColor: mhdStats.expired_today > 0 ? 'bg-red-50' : (mhdStats.expiring_soon > 0 ? 'bg-yellow-50' : 'bg-orange-50'),
      onClick: () => navigate('/employee/mhd-control'),
      badge: mhdStats.expired_today > 0 ? mhdStats.expired_today : null
    },
    {
      title: 'Abschriften',
      value: '→',
      subtitle: 'Warenverluste',
      icon: ClipboardMinus,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      onClick: () => navigate('/employee/abschriften')
    },
    {
      title: 'Lieferung',
      value: '→',
      subtitle: 'Lieferscheine prüfen',
      icon: Truck,
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-50',
      onClick: () => navigate('/lieferung')
    },
    {
      title: 'Bestellungen',
      value: orderCategories.length > 0 ? orderCategories.length : '→',
      subtitle: orderCategories.length > 0
        ? `${orderCategories.length} Lieferant${orderCategories.length > 1 ? 'en' : ''} • Jetzt bestellen`
        : 'Produkte bestellen',
      icon: ShoppingCart,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      onClick: () => navigate('/admin/bestellkategorien'),
    }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="employee-dashboard-title">
            {user?.role === 'stationsleitung' ? 'Stationsleitung Dashboard' : 'Mitarbeiter Dashboard'}
          </h1>
          <p className="text-muted-foreground mt-2">Willkommen! Hier ist Ihre Tagesübersicht.</p>
        </div>

        {/* ── Eincheck-Status (schichtlos: "Ich bin da") ─────────────────── */}
        {(user?.location_ids?.length || 0) > 0 && (
          <Card className={`border-2 ${activeSession
            ? 'border-green-400 bg-green-50 dark:bg-green-900/20'
            : 'border-primary/40 bg-primary/5'
          }`}>
            <CardContent className="p-4">
              {activeSession ? (
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse shrink-0" />
                    <div>
                      <p className="font-semibold text-sm text-green-800 dark:text-green-200">Eingecheckt</p>
                      <p className="text-xs text-green-700 dark:text-green-300">
                        seit {new Date(activeSession.started_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
                      </p>
                    </div>
                  </div>
                  <Button size="sm" variant="outline"
                    className="border-green-400 text-green-800 hover:bg-green-100 dark:text-green-200 gap-1.5"
                    onClick={handleEndSession} disabled={sessionLoading}
                    data-testid="checkout-btn">
                    {sessionLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogOut className="h-3.5 w-3.5" />}
                    Abmelden
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-muted-foreground/40 shrink-0" />
                    <p className="font-semibold text-sm text-muted-foreground">Noch nicht eingecheckt</p>
                  </div>
                  <Button size="sm" onClick={handleStartSession} disabled={sessionLoading}
                    className="gap-1.5" data-testid="checkin-btn">
                    {sessionLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogIn className="h-3.5 w-3.5" />}
                    Ich bin da
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* SHIFT CHECK CARD - PRIORITY (Only shown if pending) */}
        {shiftCheckPending && (
          <Card 
            className="border-red-500 border-2 bg-red-50 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate('/employee/shift-check')}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-red-100">
                    <ClipboardCheck className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-bold text-red-900">⚠️ Schicht-Check offen</CardTitle>
                    <CardDescription className="text-red-700 mt-1">
                      Verpflichtend vor Schichtende abschließen
                    </CardDescription>
                  </div>
                </div>
                <div className="text-red-600 font-bold">→</div>
              </div>
            </CardHeader>
          </Card>
        )}

        <div className="grid gap-6 md:grid-cols-3 lg:grid-cols-4">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card 
                key={index} 
                data-testid={`employee-stat-card-${index}`}
                className={`${stat.onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''} ${stat.badge ? 'border-red-300' : ''}`}
                onClick={stat.onClick}
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
                  <div className={`p-2 rounded-md ${stat.bgColor} relative`}>
                    <Icon className={`h-4 w-4 ${stat.color}`} />
                    {stat.badge && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                        {stat.badge}
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
                    {stat.value}
                  </div>
                  {stat.subtitle && (
                    <p className="text-xs text-muted-foreground mt-1">{stat.subtitle}</p>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {/* Übergabe Card */}
          <Card 
            className={`cursor-pointer hover:shadow-md transition-shadow ${pendingConfirmationsCount > 0 ? 'border-orange-300 bg-orange-50' : ''}`}
            onClick={() => navigate('/employee/handover')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Übergabe</CardTitle>
              <div className={`p-2 rounded-md ${pendingConfirmationsCount > 0 ? 'bg-orange-100' : 'bg-gray-100'}`}>
                {pendingConfirmationsCount > 0 ? (
                  <Bell className="h-4 w-4 text-orange-600" />
                ) : (
                  <ArrowLeftRight className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                )}
              </div>
            </CardHeader>
            <CardContent>
              {pendingConfirmationsCount > 0 ? (
                <>
                  <div className="text-2xl font-bold text-orange-600" style={{fontFamily: 'Manrope, sans-serif'}}>
                    {pendingConfirmationsCount}
                  </div>
                  <p className="text-xs text-orange-600 mt-1">
                    offene Bestätigung{pendingConfirmationsCount > 1 ? 'en' : ''}
                  </p>
                </>
              ) : (
                <>
                  <div className="text-2xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
                    -
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Keine offenen Übergaben
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Willkommen bei AllFlowX</CardTitle>
            <CardDescription>
              Ihre tägliche Arbeitsplattform
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Nutzen Sie das Menü links, um Ihre Aufgaben zu erledigen, Ware zu melden und mit Kollegen zu kommunizieren.
            </p>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};
